import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, makeStyles, Chip, Tooltip, CircularProgress
} from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import ScheduleIcon from '@material-ui/icons/Schedule';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import { getUserAnalyticsBreakdown } from '../services/userService';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  metricCard: {
    padding: '16px 20px',
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.8)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  metricTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  metricValue: {
    fontSize: '1.8rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    marginTop: 6,
    marginBottom: 4,
  },
  metricSub: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  growthPositive: {
    color: '#10b981',
    fontWeight: 700,
  },
  chartContainer: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  barsWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 140,
    gap: 8,
    padding: '10px 0 6px',
    overflowX: 'auto',
  },
  barCol: {
    flex: 1,
    minWidth: 28,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
  },
  barFill: {
    width: '100%',
    maxWidth: 24,
    borderRadius: '6px 6px 0 0',
    background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
    transition: 'height 0.4s ease, filter 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(1.2)',
    },
  },
  barLabel: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    marginTop: 6,
    whiteSpace: 'nowrap',
  },
  barCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
}));

export default function UserGrowthSection({ analytics: propAnalytics }) {
  const classes = useStyles();
  const [data, setData] = useState(propAnalytics || null);
  const [loading, setLoading] = useState(!propAnalytics);

  useEffect(() => {
    if (!propAnalytics) {
      setLoading(true);
      getUserAnalyticsBreakdown().then((res) => {
        setData(res);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      setData(propAnalytics);
      setLoading(false);
    }
  }, [propAnalytics]);

  if (loading && !data) {
    return (
      <Card className={classes.card} elevation={0}>
        <CardContent style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
          <CircularProgress size={32} style={{ color: '#10b981' }} />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            Loading user growth telemetry...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { growth, dailyTrend, platforms, fcmActiveCount, fcmActiveRate, totalUsers } = data;

  // Find max value in daily trend to scale chart bars
  const maxDay = Math.max(1, ...(dailyTrend || []).map((d) => d.newUsers || 0));

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent style={{ padding: 24 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" style={{ gap: 12 }}>
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <TrendingUpIcon style={{ color: '#10b981', fontSize: 26 }} />
            <div>
              <Typography variant="h6" style={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                User Growth & Registration Velocity
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Daily acquisition rate, active retention, and push notification reachability
              </Typography>
            </div>
          </Box>

          <Box display="flex" style={{ gap: 8 }} flexWrap="wrap">
            <Chip
              icon={<NotificationsActiveIcon style={{ fontSize: 14, color: '#10b981' }} />}
              label={`Push Active: ${fcmActiveCount || 0} (${fcmActiveRate || 0}%)`}
              size="small"
              style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', fontWeight: 700 }}
            />
            <Chip
              icon={<ScheduleIcon style={{ fontSize: 14, color: '#38bdf8' }} />}
              label={`Active Today: ${growth?.activeToday || 0}`}
              size="small"
              style={{ background: 'rgba(56,189,248,0.12)', color: '#38bdf8', fontWeight: 700 }}
            />
          </Box>
        </Box>

        {/* Metric Cards Grid */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <div className={classes.metricCard}>
              <div className={classes.metricTitle}>
                <PeopleIcon fontSize="small" style={{ color: '#10b981' }} />
                New Today
              </div>
              <div className={classes.metricValue}>
                +{growth?.today || 0}
              </div>
              <div className={classes.metricSub}>
                Yesterday: <strong>+{growth?.yesterday || 0}</strong>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <div className={classes.metricCard}>
              <div className={classes.metricTitle}>
                <TrendingUpIcon fontSize="small" style={{ color: '#0ea5e9' }} />
                Last 7 Days
              </div>
              <div className={classes.metricValue} style={{ color: '#0ea5e9' }}>
                +{growth?.last7Days || 0}
              </div>
              <div className={classes.metricSub}>
                Avg ~{Math.round((growth?.last7Days || 0) / 7)} users / day
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <div className={classes.metricCard}>
              <div className={classes.metricTitle}>
                <ScheduleIcon fontSize="small" style={{ color: '#8b5cf6' }} />
                Last 30 Days
              </div>
              <div className={classes.metricValue} style={{ color: '#8b5cf6' }}>
                +{growth?.last30Days || 0}
              </div>
              <div className={classes.metricSub}>
                Monthly acquisition growth
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <div className={classes.metricCard}>
              <div className={classes.metricTitle}>
                <PhoneAndroidIcon fontSize="small" style={{ color: '#f59e0b' }} />
                Platform Share
              </div>
              <div className={classes.metricValue} style={{ fontSize: '1.25rem', marginTop: 10 }}>
                🤖 {platforms?.android || 0} <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Android</span>
              </div>
              <div className={classes.metricSub}>
                🍏 {platforms?.ios || 0} iOS · 🌐 {platforms?.web || 0} Web
              </div>
            </div>
          </Grid>
        </Grid>

        {/* 14-Day Growth Visual Trend Chart */}
        {dailyTrend && dailyTrend.length > 0 && (
          <div className={classes.chartContainer}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                📊 Daily Registrations — Past 14 Days
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Hover bar to inspect registrations & daily active users
              </Typography>
            </Box>

            <div className={classes.barsWrapper}>
              {dailyTrend.map((d, index) => {
                const heightPercent = maxDay > 0 ? Math.max(8, Math.round((d.newUsers / maxDay) * 100)) : 8;
                return (
                  <div key={index} className={classes.barCol}>
                    <span className={classes.barCount}>{d.newUsers}</span>
                    <Tooltip
                      title={`${d.dateStr} (${d.dayName}): +${d.newUsers} New Registrations, ${d.activeUsers || 0} Active Users`}
                      arrow
                      placement="top"
                    >
                      <div
                        className={classes.barFill}
                        style={{
                          height: `${heightPercent}%`,
                          background: index === dailyTrend.length - 1
                            ? 'linear-gradient(180deg, #38bdf8 0%, #0284c7 100%)'
                            : 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                        }}
                      />
                    </Tooltip>
                    <span className={classes.barLabel}>{d.dateStr}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
